/**
 * تاریخچه‌ی تغییرهای سیگنال — قرینه‌ی apps/mobile/app/signal/versions/[id].tsx.
 *
 * دیفالتِ کاربر همون صفحه‌ی سیگناله که همیشه نسخه‌ی فعلی رو نشون می‌ده؛
 * این‌جا گذشته‌ست: تازه‌ترین بالا، هر نسخه با فرقش نسبت به قبلی، یه نشانِ
 * «الان» روی نسخه‌ی جاری و یه نشانِ «به این جواب دادی» روی نسخه‌ی خودت.
 *
 * برای **همه‌ی** کسانیه که سیگنال رو می‌بینن، نه فقط سازنده — کسی که جواب
 * داده باید بتونه ببینه از اون‌موقع چی عوض شده. سیاستِ `sv_sel` هم دقیقاً
 * همین رو اجازه می‌ده و نه بیشتر.
 */
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { signalRepo } from '@/core/data/signalRepo';
import { tagRepo } from '@/core/data/miscRepo';
import { useSession } from '@/core/session';
import { t, useLocale } from '@/core/lib/i18n';
import { formatDateTime } from '@/core/lib/dates';
import { diffSignalVersions, readableDiffValue, versionWhen } from '@/core/lib/signalDiff';
import { Card } from '@/ui/kit';

export function SignalVersionsPage() {
  useLocale();
  const { id } = useParams<{ id: string }>();
  const { profile } = useSession();

  const signal = useQuery({
    queryKey: ['signal', id],
    queryFn: () => signalRepo.byId(id!, profile!.id),
    enabled: !!id && !!profile,
  });
  const versions = useQuery({
    queryKey: ['signalVersions', id],
    queryFn: () => signalRepo.versions(id!),
    enabled: !!id,
  });
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagRepo.list });

  if (versions.isLoading || signal.isLoading) {
    return <div className="feed"><p className="dim">…</p></div>;
  }
  if (!signal.data) {
    return <div className="feed"><Card><p className="dim">{t('error')}</p></Card></div>;
  }

  const all = versions.data ?? [];
  // ریپو قدیمی‌ترین‌اول می‌ده (ترتیبِ اتفاق)؛ نمایش برعکسه چون آدم از «الان»
  // شروع می‌کنه و عقب می‌ره.
  const shown = [...all].reverse();
  const mine = signal.data.myRespondedVersion ?? null;

  return (
    <div className="feed">
      <Link to={`/signal/${id}`} className="dim">{t('back')}</Link>
      <h1 className="feed-title">{t('versionHistory')}</h1>
      <p className="dim">{signal.data.title}</p>

      {all.length <= 1 ? <Card><p className="dim">{t('versionsOnlyOne')}</p></Card> : null}

      {shown.map((v) => {
        const previous = all.find((x) => x.version === v.version - 1);
        const rows = previous ? diffSignalVersions(previous, v) : [];
        const isCurrent = v.version === signal.data!.version;
        return (
          <Card key={v.version} className={isCurrent ? 'confirmed' : undefined}>
            <h3>{t('versionNumber', { n: v.version })}</h3>
            <p className="dim">{formatDateTime(v.createdAt)}</p>
            <div className="chiprow">
              {isCurrent ? <span className="count ok">{t('versionCurrentMark')}</span> : null}
              {mine === v.version ? <span className="count">{t('versionYouAnswered')}</span> : null}
            </div>

            {previous ? (
              <div>
                {rows.length === 0 ? <p className="dim">{t('versionDiffNone')}</p> : rows.map((r) => (
                  <p key={r.labelKey}>
                    <small className="dim">{t(r.labelKey)}</small>
                    <br />
                    <span className="dim">{readableDiffValue(r.labelKey, r.before, tags.data ?? [])}</span>
                    {' → '}
                    <span>{readableDiffValue(r.labelKey, r.after, tags.data ?? [])}</span>
                  </p>
                ))}
              </div>
            ) : null}

            {/* خودِ نسخه، خلاصه — تا بشه بدونِ مقایسه هم خوندش. */}
            <p>{v.title}</p>
            {versionWhen(v) ? <p className="dim">{versionWhen(v)}</p> : null}
            {v.placeName ? <p className="dim">{v.placeName}</p> : null}
            {v.note ? <p className="dim">{v.note}</p> : null}
          </Card>
        );
      })}
    </div>
  );
}
